import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Search,
  Users,
  Shield,
  Ban,
  CheckCircle,
  XCircle,
  MoreVertical,
  Eye,
  Trash2,
  AlertTriangle,
  RefreshCw
} from 'lucide-react';
import { User, USER_SPORTS_OPTIONS } from '../types/models';
import { userManagementService } from '../services/userManagementService';
import { useRealtimeSubscription } from '../hooks/useRealtimeSubscription';
import { useAuth } from '../contexts/AuthContext';
import { getAssignedRegions, isRegionScopedAdmin, userMatchesAssignedRegions } from '../utils/regionAccess';
import ExpandableText from './common/ui/ExpandableText';

const formatUserDate = (value?: User['createdAt'] | User['updatedAt']) => {
  if (!value) return '';

  const normalizedDate =
    typeof value === 'string'
      ? new Date(value)
      : value instanceof Date
        ? value
        : value.toDate();

  return Number.isNaN(normalizedDate.getTime()) ? '' : normalizedDate.toLocaleDateString();
};

const formatDetailValue = (value: unknown, fallback = 'Not provided') => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'string') {
    const normalized = value.trim();
    return normalized || fallback;
  }
  if (Array.isArray(value)) {
    const normalized = value
      .map((entry) => (entry === null || entry === undefined ? '' : String(entry).trim()))
      .filter(Boolean);
    return normalized.length ? normalized.join(', ') : fallback;
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  return String(value);
};

const getUserInitial = (user: User) => {
  const primaryText = user.displayName?.trim() || user.email?.trim() || 'U';
  return primaryText.charAt(0).toUpperCase();
};

const UserAvatar: React.FC<{
  user: User;
  sizeClass: string;
  textClass: string;
}> = ({ user, sizeClass, textClass }) => {
  const [imageError, setImageError] = useState(false);
  const showImage = Boolean(user.photoURL) && !imageError;
  const labelText = user.displayName?.trim() || user.email?.trim() || 'User';

  if (showImage) {
    return (
      <img
        src={user.photoURL as string}
        alt={labelText}
        onError={() => setImageError(true)}
        className={`${sizeClass} rounded-full object-cover`}
      />
    );
  }

  return (
    <div
      className={`${sizeClass} rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-semibold select-none ${textClass}`}
      title={labelText}
    >
      {getUserInitial(user)}
    </div>
  );
};

const UserManagement: React.FC = () => {
  const { adminProfile } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | 'athlete' | 'coach' | 'organization' | 'parent'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'suspended' | 'verified'>('all');
  // 'unspecified' matches users with no sports recorded on their profile.
  const [sportsFilter, setSportsFilter] = useState<string>('all');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [actionMenuOpen, setActionMenuOpen] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLive, setIsLive] = useState(true);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const allUsers = await userManagementService.getAllUsers();
      setUsers(allUsers.filter((user) => userMatchesAssignedRegions(adminProfile, user)));
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  }, [adminProfile]);

  // Debounced refresh for real-time updates
  const debouncedRefresh = useCallback(() => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }
    refreshTimeoutRef.current = setTimeout(() => {
      console.log('[UserManagement] Real-time update detected, refreshing...');
      loadUsers();
      setLastUpdate(new Date());
    }, 500);
  }, [loadUsers]);

  // Real-time subscription to users table
  useRealtimeSubscription({
    config: { table: 'users', event: '*' },
    onChange: debouncedRefresh,
    enabled: isLive
  });

  useEffect(() => {
    loadUsers();
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [loadUsers]);

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      await loadUsers();
      return;
    }

    try {
      setLoading(true);
      const allUsers = await userManagementService.getAllUsers();
      const searchLower = searchTerm.toLowerCase();
      const searchResults = allUsers.filter(user =>
        userMatchesAssignedRegions(adminProfile, user) && (
        user.id?.toLowerCase().includes(searchLower) ||
        user.displayName?.toLowerCase().includes(searchLower) ||
        user.email?.toLowerCase().includes(searchLower) ||
        user.username?.toLowerCase().includes(searchLower) ||
        user.bio?.toLowerCase().includes(searchLower) ||
        user.location?.toLowerCase().includes(searchLower) ||
        (user.sports || []).some((sport) => sport.toLowerCase().includes(searchLower))
      ));
      setUsers(searchResults);
    } catch (error) {
      console.error('Error searching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(user => {
    if (roleFilter !== 'all' && user.role !== roleFilter) return false;
    if (statusFilter === 'active' && !user.isActive) return false;
    if (statusFilter === 'verified' && !user.isVerified) return false;
    if (sportsFilter !== 'all') {
      const sports = user.sports || [];
      if (sportsFilter === 'unspecified') {
        if (sports.length > 0) return false;
      } else if (!sports.some((sport) => sport.toLowerCase() === sportsFilter.toLowerCase())) {
        return false;
      }
    }
    return true;
  });

  const handleSuspendUser = async (user: User) => {
    const reason = prompt('Enter suspension reason:');
    if (!reason) return;

    try {
      await userManagementService.suspendUser(user.id, reason);
      await loadUsers();
      setActionMenuOpen(null);
    } catch (error) {
      console.error('Error suspending user:', error);
    }
  };

  const handleActivateUser = async (user: User) => {
    try {
      await userManagementService.activateUser(user.id);
      await loadUsers();
      setActionMenuOpen(null);
    } catch (error) {
      console.error('Error activating user:', error);
    }
  };

  const handleVerifyUser = async (user: User) => {
    try {
      await userManagementService.verifyUser(user.id);
      await loadUsers();
      setActionMenuOpen(null);
    } catch (error) {
      console.error('Error verifying user:', error);
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      setIsDeleting(true);
      await userManagementService.deleteUser(userToDelete.id, deleteReason || 'Account deleted by admin');
      await loadUsers();
      setShowDeleteConfirm(false);
      setUserToDelete(null);
      setDeleteReason('');
      setActionMenuOpen(null);
      alert('User account deleted successfully');
    } catch (error) {
      console.error('Error deleting user:', error);
      alert(`Failed to delete user: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const openDeleteConfirmation = (user: User) => {
    setUserToDelete(user);
    setShowDeleteConfirm(true);
    setActionMenuOpen(null);
  };

  const UserDetailsModal: React.FC<{ user: User; onClose: () => void }> = ({ user, onClose }) => {
    const positionValue =
      user.positionName && user.position && user.positionName !== user.position
        ? `${user.positionName} (${user.position})`
        : user.positionName || user.position;
    const subcategoryValue =
      user.subcategoryName && user.subcategory && user.subcategoryName !== user.subcategory
        ? `${user.subcategoryName} (${user.subcategory})`
        : user.subcategoryName || user.subcategory;
    const sportDetailsValue = (user.sportDetails || [])
      .map((sport) => sport?.name || sport?.id)
      .filter(Boolean)
      .join(', ');

    return (
      <div className="fixed inset-0 z-50 bg-gray-600 bg-opacity-50 overflow-y-auto p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-auto max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-xl font-bold text-gray-900">User Details</h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

          <div className="space-y-6">
            {/* Basic Info */}
            <div className="flex items-center space-x-4">
              <UserAvatar user={user} sizeClass="w-20 h-20" textClass="text-2xl" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{user.displayName}</h3>
                <p className="text-gray-600">{user.email}</p>
                <div className="flex items-center space-x-2 mt-2">
                  {user.isVerified && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Verified
                    </span>
                  )}
                  {!user.isActive && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      <Ban className="w-3 h-3 mr-1" />
                      Inactive
                    </span>
                  )}
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    user.role === 'athlete' ? 'bg-blue-100 text-blue-800' :
                    user.role === 'coach' ? 'bg-green-100 text-green-800' :
                    'bg-purple-100 text-purple-800'
                  }`}>
                    {user.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'User'}
                  </span>
                </div>
              </div>
            </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Identity & Contact</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700">User ID</label>
                    <p className="mt-1 text-sm text-gray-900 font-mono bg-gray-50 p-2 rounded border">{formatDetailValue(user.id)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">UID</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDetailValue(user.uid)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Username</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDetailValue(user.username)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <p className="mt-1 text-sm text-gray-900 break-all">{formatDetailValue(user.email)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Mobile Number</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDetailValue(user.mobile)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Alternate Phone</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDetailValue(user.phone)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Website</label>
                    <p className="mt-1 text-sm text-gray-900 break-all">{formatDetailValue(user.website)}</p>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700">Bio</label>
                    <ExpandableText text={formatDetailValue(user.bio)} limit={200} className="mt-1 text-sm text-gray-900" />
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Location & Personal</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Location</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDetailValue(user.location)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Country</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDetailValue(user.country)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">State</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDetailValue(user.state)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">City</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDetailValue(user.city)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Date of Birth</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDetailValue(user.dateOfBirth)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Gender</label>
                    <p className="mt-1 text-sm text-gray-900 capitalize">{formatDetailValue(user.gender)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Height</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDetailValue(user.height)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Weight</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDetailValue(user.weight)}</p>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Sports Profile</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Sports</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDetailValue(user.sports, 'Not specified')}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Event Types</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDetailValue(user.eventTypes, 'Not specified')}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Position</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDetailValue(positionValue, 'Not specified')}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Subcategory</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDetailValue(subcategoryValue, 'Not specified')}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Specializations</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDetailValue(user.specializations, 'Not specified')}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Sport Details</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDetailValue(sportDetailsValue, 'Not specified')}</p>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Account</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Account Status</label>
                    <p className="mt-1 text-sm text-gray-900">{user.isActive ? 'Active' : 'Inactive'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Profile Status</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDetailValue(user.status, 'Not set')}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Online</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDetailValue(user.isOnline, 'Unknown')}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Joined</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDetailValue(formatUserDate(user.createdAt), 'N/A')}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Last Updated</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDetailValue(formatUserDate(user.updatedAt), 'N/A')}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Last Seen</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDetailValue(formatUserDate(user.lastSeen as User['createdAt']), 'Not available')}</p>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700">Suspension Reason</label>
                    <ExpandableText text={formatDetailValue(user.suspension_reason, 'None')} limit={150} className="mt-1 text-sm text-gray-900" />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-2xl font-bold text-gray-900">{user.followersCount || 0}</p>
                  <p className="text-sm text-gray-600">Followers</p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-2xl font-bold text-gray-900">{user.followingCount || 0}</p>
                  <p className="text-sm text-gray-600">Following</p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-2xl font-bold text-gray-900">{user.postsCount || 0}</p>
                  <p className="text-sm text-gray-600">Posts</p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-2xl font-bold text-gray-900">{user.storiesCount || 0}</p>
                  <p className="text-sm text-gray-600">Stories</p>
                </div>
              </div>

              {(user.athleteProfile || user.privacy || user.settings) && (
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900">Additional Raw Details</h4>
                  {user.athleteProfile && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Athlete Profile JSON</label>
                      <pre className="text-xs bg-gray-900 text-gray-100 p-3 rounded-lg overflow-auto max-h-64">
                        {JSON.stringify(user.athleteProfile, null, 2)}
                      </pre>
                    </div>
                  )}
                  {user.privacy && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Privacy JSON</label>
                      <pre className="text-xs bg-gray-900 text-gray-100 p-3 rounded-lg overflow-auto max-h-64">
                        {JSON.stringify(user.privacy, null, 2)}
                      </pre>
                    </div>
                  )}
                  {user.settings && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Settings JSON</label>
                      <pre className="text-xs bg-gray-900 text-gray-100 p-3 rounded-lg overflow-auto max-h-64">
                        {JSON.stringify(user.settings, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-gray-200 pb-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
            <p className="text-gray-600 mt-1">
              {isRegionScopedAdmin(adminProfile)
                ? `Manage user accounts in ${getAssignedRegions(adminProfile).join(', ') || 'your assigned regions'}`
                : 'Manage and moderate user accounts'}
            </p>
          </div>
          <div className="flex items-center space-x-4">
            {/* Live Status */}
            <button
              onClick={() => setIsLive(!isLive)}
              className={`flex items-center space-x-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                isLive
                  ? 'bg-green-100 text-green-800 hover:bg-green-200'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <span className={`w-2 h-2 rounded-full ${isLive ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
              <span>{isLive ? 'Live' : 'Paused'}</span>
            </button>
            {lastUpdate && (
              <span className="text-xs text-gray-500">
                Updated: {lastUpdate.toLocaleTimeString()}
              </span>
            )}
            {/* Manual Refresh */}
            <button
              onClick={() => {
                loadUsers();
                setLastUpdate(new Date());
              }}
              disabled={loading}
              className="flex items-center space-x-2 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      {isRegionScopedAdmin(adminProfile) && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
          Region scope active: you can view users only from {getAssignedRegions(adminProfile).join(', ') || 'your assigned regions'}.
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-4">
          {/* Search */}
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search users by User ID, name, email, bio, sport, or location..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
          </div>

          {/* Filters */}
          <div className="flex space-x-4">
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Roles</option>
              <option value="athlete">Athletes</option>
              <option value="coach">Coaches</option>
              <option value="organization">Organizations</option>
              <option value="parent">Parents</option>
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
              <option value="verified">Verified</option>
            </select>

            <select
              value={sportsFilter}
              onChange={(e) => setSportsFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Sports</option>
              {USER_SPORTS_OPTIONS.map((sport) => (
                <option key={sport} value={sport}>{sport}</option>
              ))}
              <option value="unspecified">Not Specified</option>
            </select>

            <button
              onClick={handleSearch}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
            >
              Search
            </button>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Users ({filteredUsers.length})
          </h3>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stats
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Joined
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <img
                        className="h-10 w-10 rounded-full object-cover"
                        src={user.photoURL || 'https://via.placeholder.com/40'}
                        alt={user.displayName}
                      />
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{user.displayName}</div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          Mobile: {user.mobile || user.phone || 'Not provided'}
                        </div>
                        <div className="text-xs text-blue-600 mt-1">
                          Sport: {user.sports && user.sports.length > 0 ? user.sports.join(', ') : 'Not specified'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      user.role === 'athlete' ? 'bg-blue-100 text-blue-800' :
                      user.role === 'coach' ? 'bg-green-100 text-green-800' :
                      'bg-purple-100 text-purple-800'
                    }`}>
                      {user.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'User'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col space-y-1">
                      {user.isVerified && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Verified
                        </span>
                      )}
                      {!user.isActive && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          <Ban className="w-3 h-3 mr-1" />
                          Inactive
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex space-x-3">
                      <span>{user.followersCount || 0} followers</span>
                      <span>{user.postsCount || 0} posts</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {user.createdAt ? new Date(typeof user.createdAt === 'string' ? user.createdAt : user.createdAt instanceof Date ? user.createdAt : user.createdAt.toDate()).toLocaleDateString() : 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="relative">
                      <button
                        onClick={() => setActionMenuOpen(actionMenuOpen === user.id ? null : user.id)}
                        className="text-gray-400 hover:text-gray-600 p-1"
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                      {actionMenuOpen === user.id && (
                        <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-10">
                          <div className="py-1">
                            <button
                              onClick={() => {
                                setSelectedUser(user);
                                setShowUserDetails(true);
                                setActionMenuOpen(null);
                              }}
                              className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              <Eye className="w-4 h-4 inline mr-2" />
                              View Details
                            </button>
                            {!user.isVerified && (
                              <button
                                onClick={() => handleVerifyUser(user)}
                                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              >
                                <Shield className="w-4 h-4 inline mr-2" />
                                Verify User
                              </button>
                            )}
                            {!user.isActive ? (
                              <button
                                onClick={() => handleActivateUser(user)}
                                className="block w-full text-left px-4 py-2 text-sm text-green-700 hover:bg-gray-100"
                              >
                                <CheckCircle className="w-4 h-4 inline mr-2" />
                                Activate
                              </button>
                            ) : (
                              <button
                                onClick={() => handleSuspendUser(user)}
                                className="block w-full text-left px-4 py-2 text-sm text-red-700 hover:bg-gray-100"
                              >
                                <Ban className="w-4 h-4 inline mr-2" />
                                Suspend
                              </button>
                            )}
                            <button
                              onClick={() => openDeleteConfirmation(user)}
                              className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 border-t border-gray-200"
                            >
                              <Trash2 className="w-4 h-4 inline mr-2" />
                              Delete Account
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredUsers.length === 0 && (
          <div className="text-center py-12">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Try adjusting your search or filter criteria.
            </p>
          </div>
        )}
      </div>

      {/* User Details Modal */}
      {showUserDetails && selectedUser && (
        <UserDetailsModal
          user={selectedUser}
          onClose={() => {
            setShowUserDetails(false);
            setSelectedUser(null);
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && userToDelete && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>

              <h3 className="mt-4 text-lg font-medium text-center text-gray-900">
                Delete Account
              </h3>

              <p className="mt-2 text-sm text-center text-gray-500">
                Are you sure you want to permanently delete the account for{' '}
                <strong>{userToDelete.displayName || userToDelete.email}</strong>?
              </p>

              <p className="mt-2 text-xs text-center text-gray-400">
                This action cannot be undone.
              </p>

              {/* Reason Input */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Deletion Reason (optional)
                </label>
                <textarea
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  placeholder="Enter reason for deletion..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  rows={3}
                  disabled={isDeleting}
                />
              </div>

              {/* Action Buttons */}
              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setUserToDelete(null);
                    setDeleteReason('');
                  }}
                  disabled={isDeleting}
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteUser}
                  disabled={isDeleting}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDeleting ? 'Deleting...' : 'Delete Account'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
